import React, { useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { User as FirebaseUser } from 'firebase/auth';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useGetMcpPendingConsentQuery } from '../../store/mcpOAuthApi';

// The API base already ends in /api.
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

// Vendor-app side of the MCP OAuth 2.1 consent handoff (docs/MCP_SERVER_PLAN.md D9).
// The authorization server redirects the browser here with a one-time request_id; after
// the vendor approves, the backend seats the AS session and returns the URL to resume the
// OAuth flow back to the connector (Claude / ChatGPT).
const SESSION_RETURN_KEY = 'mcp_oauth_return';

const Spinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
  </div>
);

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      {children}
    </div>
  </div>
);

const Notice: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <Shell>
    <div className="flex flex-col items-center text-center gap-3">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      <p className="text-sm text-gray-500">{body}</p>
    </div>
  </Shell>
);

const ConsentCard: React.FC<{ requestId: string; firebaseUser: FirebaseUser | null }> = ({
  requestId,
  firebaseUser,
}) => {
  const { t } = useTranslation();
  const { data: pending, isLoading, isError } = useGetMcpPendingConsentQuery(requestId);
  const [submitting, setSubmitting] = useState<null | 'approve' | 'deny'>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (isError || !pending) {
    return <Notice title={t('mcpConsent.expired.title')} body={t('mcpConsent.expired.body')} />;
  }

  const decide = async (approve: boolean) => {
    setActionError(null);
    setSubmitting(approve ? 'approve' : 'deny');
    try {
      const idToken = await firebaseUser?.getIdToken();
      if (!idToken) throw new Error('no-token');
      // MUST be credentialed: the backend seats an OAuth session and Set-Cookies a
      // JSESSIONID here; without credentials:'include' the browser drops that cookie,
      // the resumed /oauth2/authorize sees no session, and the flow loops back to this
      // page. Same-site (localhost / *.clareefai.com) so the cookie rides the resume nav.
      const res = await fetch(`${API_BASE}/mcp-oauth/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, idToken, approve }),
      });
      if (!res.ok) {
        setSubmitting(null);
        setActionError(res.status === 403 ? t('mcpConsent.error.forbidden') : t('mcpConsent.error.generic'));
        return;
      }
      const { redirectUrl } = (await res.json()) as { redirectUrl: string };
      // Top-level navigation back to the AS carries the same-site session cookie.
      window.location.href = redirectUrl;
    } catch {
      setSubmitting(null);
      setActionError(t('mcpConsent.error.generic'));
    }
  };

  return (
    <Shell>
      <div className="flex flex-col items-center text-center gap-4">
        <div className="h-12 w-12 rounded-full bg-purple-50 flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-purple-600" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">
          {t('mcpConsent.title', { client: pending.clientName })}
        </h1>
        <p className="text-sm text-gray-500">
          {t('mcpConsent.subtitle', { client: pending.clientName })}
        </p>

        <div className="w-full text-left bg-gray-50 rounded-lg p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
            {t('mcpConsent.scopesLabel')}
          </p>
          <ul className="space-y-1">
            {pending.scopes.map((scope) => (
              <li key={scope} className="flex items-center gap-2 text-sm text-gray-700">
                <ShieldCheck className="h-4 w-4 text-green-500 shrink-0" />
                {t(`mcpConsent.scope.${scope}`, { defaultValue: scope })}
              </li>
            ))}
          </ul>
        </div>

        {actionError && <p className="text-sm text-red-600">{actionError}</p>}

        <div className="flex w-full gap-3 pt-2">
          <button
            type="button"
            onClick={() => decide(false)}
            disabled={submitting !== null}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {submitting === 'deny' ? t('mcpConsent.working') : t('mcpConsent.deny')}
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            disabled={submitting !== null}
            className="flex-1 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {submitting === 'approve' ? t('mcpConsent.working') : t('mcpConsent.allow')}
          </button>
        </div>
        <p className="text-xs text-gray-400">{t('mcpConsent.footer')}</p>
      </div>
    </Shell>
  );
};

const McpConsent: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, firebaseUser } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request_id');

  // Wait for auth to settle, then bounce unauthenticated users through login while
  // preserving the full return URL (request_id lives in the query string).
  if (isLoading) return <Spinner />;
  if (!isAuthenticated) {
    sessionStorage.setItem(SESSION_RETURN_KEY, `${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }
  if (!requestId) {
    return <Notice title={t('mcpConsent.invalid.title')} body={t('mcpConsent.invalid.body')} />;
  }

  return <ConsentCard requestId={requestId} firebaseUser={firebaseUser} />;
};

export default McpConsent;
